import { reactive } from 'vue'
import { DateTime, Duration } from 'luxon'
import { AccessTokenResponse } from './AccessTokenResponse'
import { LocalTokenApi } from './LocalTokenApi'
import { ApiError } from '@webapp/util/Api'
import { AsyncUtil } from '@webapp/util/AsyncUtil'
import { DurationUtil } from '@webapp/util/LuxonUtil'
import { useTelemetry } from '@webapp/util/Telemetry'
import jwtDecode, { JwtPayload } from 'jwt-decode'

interface TokenPayload extends JwtPayload {
    scope?: string
    profile_id?: string
    is_supervised?: boolean
    act?: {
        sub: string
    }
}

const oldAccessTokenKey = 'tc-access-token'
const oldAccessTokenExpiresKey = 'tc-access-token-expires'

const oldRefreshTokenStorageKey = 'tc-refresh-token'
const oldRefreshTokenExpiresStorageKey = 'tc-refresh-token-expires'

const oldIdentityTokenStorageKey = "tc-identity-token"
const oldIdentityTokenExpiresStorageKey = "tc-identity-token-expires"

const oldStorageKeys = [
    oldAccessTokenKey,
    oldAccessTokenExpiresKey,
    oldRefreshTokenStorageKey,
    oldRefreshTokenExpiresStorageKey,
    oldIdentityTokenStorageKey,
    oldIdentityTokenExpiresStorageKey,
    'tc-user-id',
    'tc-actor-id',
    'tc-user-profile-id',
    'tc-user-privileges',
    'tc-is-supervised',
]

const expiredDate = DateTime.fromISO('2000-01-01') // forever in the past, used for expired tokens

const earlyRefreshInterval = DurationUtil.fromReadable('00:15:00')
const noTokenRefreshDelay = DurationUtil.fromReadable('00:05:00')
const refreshClientErrorDelay = DurationUtil.fromReadable('00:05:00') // unlikely to resolve, use slow retry
const refreshServerErrorDelay = DurationUtil.fromReadable('00:00:30') // could resolve quickly, use fast retry
const maxWaitForRefresh = DurationUtil.fromReadable('00:00:05')

function toExpirationDate(expireDateStr?: string): DateTime {
    return expireDateStr ? DateTime.fromISO(expireDateStr) : expiredDate
}

export enum UserPrivilege {
    User = 'User',
    ReadAllUsers = 'ReadAllUsers',
    ReadAllDataGroups = 'ReadAllDataGroups',
    ReadAllFamilyGroups = 'ReadAllFamilyGroups',
    ImpersonateUser = 'ImpersonateUser',
    ManageUsers = 'ManageUsers',
    DemoUser = 'DemoUser',
    ReadAllTestData = 'ReadAllTestData',
}

export function getPrivilegeDisplayName(privilege: UserPrivilege) {
    switch (privilege) {
        case UserPrivilege.User: return 'Standard user'
        case UserPrivilege.ReadAllUsers: return 'Read all users'
        case UserPrivilege.ReadAllDataGroups: return 'Read all data groups'
        case UserPrivilege.ReadAllFamilyGroups: return 'Read all family groups'
        case UserPrivilege.ManageUsers: return 'Manage users'
        case UserPrivilege.ImpersonateUser: return 'Impersonate user'
    }
}

export interface AccessTokenInfo {
    token: string
    isValid: boolean
    userId?: string
    actorId?: string
    userProfileId?: string
    userPrivileges: UserPrivilege[]
    isPrivilegedUser: boolean
    canReadUsers: boolean
    isSupervised: boolean
    canRefresh: boolean
    hasPrivilege(privilege: UserPrivilege): boolean
}

export class UserTokenInfo implements AccessTokenInfo {
    token = ''
    expires = expiredDate
    userId = ''
    actorId?: string
    userProfileId = ''
    userPrivileges: UserPrivilege[] = []
    isSupervised = false
    refreshToken?: string
    refreshTokenExpires = expiredDate
    identityToken?: string
    identityTokenExpires = expiredDate

    /**
     * Returns true if an unexpired access token is present.
     */
    get isValid() { return !!this.token && DateTime.utc() < this.expires }

    /**
     * Returns true if both an access token (expired okay) and a refresh token (unexpired) are present.
     */
    get canRefresh() {
        return !!this.token && !!this.refreshToken && this.refreshTokenExpires > DateTime.utc()
    }

    get isPrivilegedUser() { 
        return this.userPrivileges.some(p => p && p != UserPrivilege.User && p != UserPrivilege.DemoUser)
    }

    get canReadUsers() {
        return this.hasPrivilege(UserPrivilege.ReadAllUsers) || this.hasPrivilege(UserPrivilege.ReadAllTestData)
    }

    hasPrivilege(privilege: UserPrivilege) {
        return this.userPrivileges.includes(privilege)
    }
}

const userKeyPrefix = 'tc.users.'

const currentUserKey = userKeyPrefix + 'current'

const accessTokenKeyPattern = /^tc\.users\.(\w+)\.accessToken$/ // userKeyPrefix + userId + '.' + accessTokenSubkey
const accessTokenSubkey = 'accessToken'
const accessTokenExpiresSubkey = 'accessTokenExpires'
const refreshTokenSubkey = 'refreshToken'
const refreshTokenExpiresSubkey = 'refreshTokenExpires'
const identityTokenSubkey = 'identityToken'
const identityTokenExpiresSubkey = 'identityTokenExpires'

const allTokenSubkeys = [
    accessTokenSubkey,
    accessTokenExpiresSubkey,
    refreshTokenSubkey,
    refreshTokenExpiresSubkey,
    identityTokenSubkey,
    identityTokenExpiresSubkey    
]

let instance: ReturnType<typeof createInstance> | undefined

export function useTokenManager() {
    // lazy initialization so it can be done in the top-level component context
    if (!instance) {
        instance = createInstance()
    }
    return instance
}

function createInstance() {

    const { trackEvent, trackException } = useTelemetry()

    migrateOldTokenInfo()

    const savedUserIds = [...getSavedUserIds()]
    const currentTokenInfo = reactive(new UserTokenInfo()) // empty by default
    loadCurrentTokenInfo()
    
    function buildTokenKey(subkey: string, userId: string) {
        return userKeyPrefix + userId + '.' + subkey
    }

    function getStoredTokenValue(subkey: string, userId: string) {
        const key = buildTokenKey(subkey, userId)
        return localStorage.getItem(key) ?? undefined
    }

    function setStoredTokenValue(subkey: string, value: string, userId: string) {
        const key = buildTokenKey(subkey, userId)
        localStorage.setItem(key, value)
    }

    function removeStoredTokenValue(subkey: string, userId: string) {
        const key = buildTokenKey(subkey, userId)
        localStorage.removeItem(key)
    }

    function migrateOldTokenInfo() {
        const oldAccessToken = localStorage.getItem(oldAccessTokenKey)
        const oldAccessTokenExpires = localStorage.getItem(oldAccessTokenExpiresKey)
        if (oldAccessToken && oldAccessTokenExpires) {
            const oldRefreshToken = localStorage.getItem(oldRefreshTokenStorageKey) ?? undefined
            const oldRefreshExpires = localStorage.getItem(oldRefreshTokenExpiresStorageKey) ?? undefined
            const oldIdentityToken = localStorage.getItem(oldIdentityTokenStorageKey) ?? undefined
            const oldIdentityTokenExpires = localStorage.getItem(oldIdentityTokenExpiresStorageKey) ?? undefined

            const tokenInfo = toUserTokenInfo(oldAccessToken, oldAccessTokenExpires, oldRefreshToken, oldRefreshExpires, oldIdentityToken, oldIdentityTokenExpires)
            saveTokenInfo(tokenInfo)
            localStorage.setItem(currentUserKey, tokenInfo.userId)

            // cleanup old storage entries
            oldStorageKeys.forEach(key => localStorage.removeItem(key))
        }
    }

    function *getSavedUserIds() {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)!
            const match = key.match(accessTokenKeyPattern)
            if (match) {
                yield match[1]
            }
        }
    }

    function loadCurrentTokenInfo() {
        const currentUserId = localStorage.getItem(currentUserKey)
        if (currentUserId) {
            trySetCurrentToken(currentUserId)
        }
    }

    function tryGetStoredTokenInfo(userId: string) {
        const token = getStoredTokenValue(accessTokenSubkey, userId)
        const expiresStr = getStoredTokenValue(accessTokenExpiresSubkey, userId)

        if (token && expiresStr) {
            const refreshToken = getStoredTokenValue(refreshTokenSubkey, userId)
            const refreshExpires = getStoredTokenValue(refreshTokenExpiresSubkey, userId)
            const identityToken = getStoredTokenValue(identityTokenSubkey, userId)
            const identityTokenExpires = getStoredTokenValue(identityTokenExpiresSubkey, userId)

            const tokenInfo = toUserTokenInfo(token, expiresStr, refreshToken, refreshExpires, identityToken, identityTokenExpires)
            if (tokenInfo.userId != userId) {
                trackEvent('TokenManager.UserMismatch', { key: userId, tokenUserId: tokenInfo?.userId })
                return
            }
            return tokenInfo
        }
    }

    function toUserTokenInfo(token: string, expires: string, refreshToken?: string, refreshExpires?: string, identityToken?: string, identityTokenExpires?: string) {
        const tokenObj = jwtDecode<TokenPayload>(token)
        const tokenInfo = new UserTokenInfo()

        if (!tokenObj.sub || !tokenObj.profile_id)
            throw new Error(`TokenManager.InvalidToken: ${token.slice(-5)}`)

        tokenInfo.token = token
        tokenInfo.expires = toExpirationDate(expires)
        tokenInfo.userId = tokenObj.sub ?? 'bad-subject'
        tokenInfo.actorId = tokenObj.act?.sub
        tokenInfo.userProfileId = tokenObj.profile_id ?? 'bad-profile-id'
        tokenInfo.userPrivileges = tokenObj.scope?.split(' ').filter(s => s != 'all') as UserPrivilege[] ?? []
        tokenInfo.isSupervised = tokenObj.is_supervised ?? false
        tokenInfo.refreshToken = refreshToken ?? undefined
        tokenInfo.refreshTokenExpires = toExpirationDate(refreshExpires)
        tokenInfo.identityToken = identityToken ?? undefined
        tokenInfo.identityTokenExpires = toExpirationDate(identityTokenExpires)
        return tokenInfo
    }

    function saveTokenInfo(tokenInfo: UserTokenInfo) {
        setStoredTokenValue(accessTokenSubkey, tokenInfo.token, tokenInfo.userId)
        setStoredTokenValue(accessTokenExpiresSubkey, tokenInfo.expires.toISO() ?? '', tokenInfo.userId)
        setStoredTokenValue(refreshTokenSubkey, tokenInfo.refreshToken ?? '', tokenInfo.userId)
        setStoredTokenValue(refreshTokenExpiresSubkey, tokenInfo.refreshTokenExpires.toISO() ?? '', tokenInfo.userId)
        setStoredTokenValue(identityTokenSubkey, tokenInfo.identityToken ?? '', tokenInfo.userId)
        setStoredTokenValue(identityTokenExpiresSubkey, tokenInfo.identityTokenExpires.toISO() ?? '', tokenInfo.userId)

        trackEvent("TokenManager.AccessTokenSaved", { 
            token: `...${tokenInfo.token.slice(-5)}`, 
            expires: tokenInfo.expires.toLocal().toLocaleString(DateTime.TIME_WITH_SECONDS)
        })
        if (tokenInfo.refreshToken) {
            trackEvent("TokenManager.RefreshTokenSaved", { 
                token: `...${tokenInfo.refreshToken.slice(-5)}`, 
                expires: tokenInfo.refreshTokenExpires.toLocal().toLocaleString(DateTime.TIME_WITH_SECONDS)
            })
        }
    }

    function trySetCurrentToken(userId: string) {
        const userTokenInfo = tryGetStoredTokenInfo(userId)
        if (userTokenInfo) {
            localStorage.setItem(currentUserKey, userTokenInfo.userId)
            Object.assign(currentTokenInfo, userTokenInfo)
            return true
        }
        return false
    }

    function clearCurrentToken() {
        if (currentTokenInfo.userId) {
            allTokenSubkeys.forEach(subkey => removeStoredTokenValue(subkey, currentTokenInfo.userId))
            localStorage.removeItem(currentUserKey)

            Object.assign(currentTokenInfo, new UserTokenInfo()) // empty
            
            // NOTE: preserve currentUserId in case user decides to re-authenticate
        }
    }

    function clearSavedTokens() {
        for (const userId of getSavedUserIds()) {
            allTokenSubkeys.forEach(subkey => removeStoredTokenValue(subkey, userId))
        }
    }

    function setCurrentTokenFromResponse(response: AccessTokenResponse) {
        const token = response.access_token
        const tokenObj = jwtDecode<TokenPayload>(token)
        const expires = DateTime.fromSeconds(tokenObj.exp!).toISO()! // ignore response.expires_in
        const refreshToken = response.refresh_token
        const refreshExpires = DateTime.utc().plus({ seconds: response.refresh_token_expires_in }).toISO()!

        const newTokenInfo = toUserTokenInfo(token, expires, refreshToken, refreshExpires)
        saveTokenInfo(newTokenInfo)
        if (!trySetCurrentToken(newTokenInfo.userId))
            throw new Error('Failed to set current token')
    }

    /**
     * Gets a valid access token (if possible) for the current user, waiting a short time for token refresh if necessary.
     */
    async function getAccessTokenAsync() {
        if (currentTokenInfo.isValid)
            return currentTokenInfo.token

        if (currentTokenInfo.canRefresh) {
            // auto-refresh should be in progress, so just wait a bit for it to succeed

            // NOTE: Having an invalid but refreshable access token should only occur if app has been idle, and 
            // as a result auto-refresh was paused long enough for the current token to expire. Normally, refresh 
            // occurs well before the token expires, so a valid token is always available.

            const start = DateTime.utc()
            while (DateTime.utc().diff(start) < maxWaitForRefresh) {
                await AsyncUtil.delay(200) // try to respond ASAP
                if (currentTokenInfo.isValid) {
                    return currentTokenInfo.token
                }
            }
        }
    }

    /**
     * Gets a token that can be used with the identity service to get identity information for the current user.
     */
    async function getIdentityTokenAsync() {
        if (currentTokenInfo.token) {
            if (currentTokenInfo.identityToken && DateTime.utc() < currentTokenInfo.identityTokenExpires.minus({ minutes: 5 })) {
                return currentTokenInfo.identityToken
            }
            else {
                const response = await LocalTokenApi.getLocalIdentityTokenAsync(currentTokenInfo.token)
                currentTokenInfo.identityToken = response.access_token
                currentTokenInfo.identityTokenExpires = DateTime.utc().plus({ seconds: response.expires_in })
                saveTokenInfo(currentTokenInfo)                
                return currentTokenInfo.identityToken
            }
        }
    }

    async function exchangeToken(identityToken: string) {
        const response = await LocalTokenApi.getLocalAccessTokenAsync(identityToken)
        
        trackEvent('TokenManager.IdentityTokenExchanged')
        setCurrentTokenFromResponse(response)
    }

    function canImpersonateAnyUser() {
        return currentTokenInfo.isValid &&
            !currentTokenInfo.actorId && // not already impersonating
            currentTokenInfo.hasPrivilege(UserPrivilege.ImpersonateUser)
    }

    async function impersonateAsync(userId: string) {
        if (!currentTokenInfo.isValid)
            throw 'Cannot impersonate user without a valid access token'

        const actorId = currentTokenInfo.userId
        const response = await LocalTokenApi.getLocalAccessTokenForUserAsync(currentTokenInfo.token!, userId)

        trackEvent('TokenManager.UserImpersonated', { actorId, userId })
        if (!canImpersonateAnyUser()) {
            // SECURITY: must be a supervisor -- do NOT allow supervised to revert to supervisor
            clearSavedTokens()
        }
        setCurrentTokenFromResponse(response)
    }

    function revert() {
        if (!currentTokenInfo.actorId)
            throw new Error('No current actor')

        if (!trySetCurrentToken(currentTokenInfo.actorId))
            throw new Error ('Failed to revert to current actor')
    }

    async function ensureRefreshedAsync() {
        if (!currentTokenInfo.isValid && currentTokenInfo.canRefresh) {
            console.log('Access token expired, can refresh, refreshing now')
            await refreshAsync()
            return true
        }
        return false
    }

    async function refreshAsync() {
        const response = await LocalTokenApi.refreshAccessToken(currentTokenInfo.token, currentTokenInfo.refreshToken!)
        trackEvent('TokenManager.TokenRefreshed')
        setCurrentTokenFromResponse(response)
    }

    async function startAutoRefresh() {
        // this method returns a promise that never resolves

        // eslint-disable-next-line no-constant-condition
        while (true) {
            if (currentTokenInfo.canRefresh) {

                // time to refresh?
                const remaining = currentTokenInfo.expires.diffNow()
                const refreshDelay = remaining.minus(earlyRefreshInterval)
                if (refreshDelay > Duration.fromMillis(0)) {
                    // nope, not yet
                    console.log(`autoRefresh: Unexpired token present, waiting ${refreshDelay.toFormat('hh:mm:ss')} before refresh`)
                    await AsyncUtil.delay(refreshDelay.as('milliseconds'))
                    continue // restart loop (don't assume anything about token state after delay)
                }

                trackEvent('TokenManager.AutoRefreshAttempt', {
                    expiringToken: `...${currentTokenInfo.token!.slice(-5)}`,
                    refreshToken: `...${currentTokenInfo.refreshToken!.slice(-5)}`,
                    expires: currentTokenInfo.expires.toLocal().toLocaleString(DateTime.TIME_WITH_SECONDS),
                })

                try {
                    await refreshAsync()

                    // sanity check
                    const newRemaining = currentTokenInfo.expires.diffNow()
                    if (newRemaining < earlyRefreshInterval) {
                        trackEvent('TokenManager.AccessTokenLifetimeTooShort', {
                            expiresIn: newRemaining.toFormat('hh:mm:ss')
                        })
                        break
                    }
                } catch (error) {
                    trackException(error)
                    const isClientError = error instanceof ApiError && error.statusCode >= 400 && error.statusCode < 500
                    const retryDelay = isClientError
                        ? refreshClientErrorDelay
                        : refreshServerErrorDelay
                    await AsyncUtil.delay(retryDelay.as('milliseconds'))
                }
            }
            else {
                console.log(`autoRefresh: No token to refresh, will check again in ${noTokenRefreshDelay.toFormat('hh:mm:ss')}`)
                const remaining = currentTokenInfo.expires.diffNow()
                if (remaining < Duration.fromMillis(0)) {
                    if (currentTokenInfo.actorId) {
                        revert()
                    }
                    else {
                        clearCurrentToken() // cleanup expired token
                    }
                }
                await AsyncUtil.delay(noTokenRefreshDelay.as('milliseconds'))
            }
        }
    }

    return {
        current: currentTokenInfo as Readonly<AccessTokenInfo>,

        clearCurrentToken,
        setCurrentTokenFromResponse,
        getAccessTokenAsync,
        getIdentityTokenAsync,
        exchangeToken,
        canImpersonateAnyUser,
        impersonateAsync,
        revert,
        ensureRefreshedAsync,
        refreshAsync,
        startAutoRefresh,
    }
}
